import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, AlertCircle, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { AuthCard } from "./ForgotPasswordPage";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ checking: true, email: "", invalid: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then((r) => setState({ checking: false, email: r.data.email, invalid: "" }))
      .catch((e) => setState({ checking: false, email: "", invalid: formatApiError(e.response?.data?.detail) || "El enlace no es válido" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Las contraseñas no coinciden"); return; }
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo actualizar la contraseña");
    } finally {
      setLoading(false);
    }
  };

  if (state.checking) {
    return <div className="min-h-screen grid place-items-center"><div className="spinner" style={{ borderColor: "#11182733", borderTopColor: "#111827" }}/></div>;
  }
  if (state.invalid) {
    return (
      <AuthCard icon={AlertCircle} title="Enlace no disponible" subtitle="No se puede restablecer la contraseña" testid="reset-invalid-card">
        <p className="text-sm text-gray-600 font-semibold mb-4" data-testid="reset-invalid-message">{state.invalid}</p>
        <button className="btn primary w-full" onClick={() => navigate("/forgot-password")} data-testid="reset-request-new-btn">
          Solicitar un nuevo enlace
        </button>
      </AuthCard>
    );
  }
  return (
    <AuthCard icon={ShieldCheck} title="Crea tu nueva contraseña" subtitle={state.email} testid="reset-card">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label-up block mb-1.5">Nueva contraseña (mín. 8 caracteres)</label>
          <div className="relative">
            <input type={show ? "text" : "password"} className="text-input w-full pr-10" value={password} required autoFocus
                   onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="reset-password-input"/>
            <button type="button" onClick={() => setShow((s) => !s)} aria-label="Mostrar contraseña" data-testid="reset-toggle-visibility"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-900">
              {show ? <EyeOff size={14}/> : <Eye size={14}/>}
            </button>
          </div>
        </div>
        <div>
          <label className="label-up block mb-1.5">Confirma la contraseña</label>
          <input type={show ? "text" : "password"} className="text-input w-full" value={confirm} required
                 onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="reset-confirm-input"/>
        </div>
        {error && (
          <div className="pill bad flex items-center gap-2 w-full" style={{ padding: "8px 12px" }} data-testid="reset-error">
            <AlertCircle size={14}/> {error}
          </div>
        )}
        <button type="submit" className="btn primary w-full flex items-center justify-center gap-2"
                disabled={loading || !password || !confirm} data-testid="reset-submit-btn">
          {loading ? <span className="spinner"/> : null}
          {loading ? "Guardando..." : "Guardar contraseña"}
        </button>
      </form>
    </AuthCard>
  );
}
